import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useLangPrefix, withLangPrefix } from '../hooks/useLangPrefix';
import { Button } from '../components/ui/button';
import { ArrowRight, Clock, ChevronRight } from 'lucide-react';

const CATEGORY_LABELS = {
  all: 'Tous les articles',
  automobile: 'Sellerie automobile',
  nautique: 'Sellerie nautique',
  moto: 'Sellerie moto',
  habitat: 'Habitat & médical',
  metier: 'Métier & atelier',
  general: 'Édito',
};

const CATEGORIES = ['all', 'automobile', 'nautique', 'moto', 'habitat', 'metier'];

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch (_) { return ''; }
};

export const BlogPage = () => {
  const { lang } = useLangPrefix();
  const L = (p) => withLangPrefix(p, lang);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const data = await api.listBlogPosts({ category, limit: 60 });
        if (mounted) setPosts(data || []);
      } catch (_) {
        if (mounted) setPosts([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [category]);

  // SEO meta — set document.title from the page only (JSON-LD on post page)
  useEffect(() => {
    document.title = 'Blog — Sellerie, réemploi & savoir-faire artisanal | Sellerie Académie';
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF7F0]" data-testid="blog-page">
      {/* Hero — même esthétique que la page formation */}
      <section className="pt-32 pb-8 md:pt-36 md:pb-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-2 text-xs text-[#4A5568] mb-4">
            <Link to={L('/')} className="hover:text-[#C9B896] transition-colors">Accueil</Link>
            <ChevronRight className="w-3 h-3 text-[#C9B896]" />
            <span className="text-[#1F2A4A]">Blog</span>
          </div>

          <div className="grid grid-cols-12 gap-6 items-end">
            <div className="col-span-12 md:col-span-8">
              <h1 className="font-light text-[#1F2A4A] leading-[1.05] tracking-tight" data-testid="blog-title">
                <span className="text-3xl md:text-5xl">Refaire</span>{' '}
                <span className="font-serif italic text-3xl md:text-5xl text-[#C9B896]">plutôt</span>{' '}
                <span className="text-3xl md:text-5xl">que racheter.</span>
              </h1>
              <p className="text-[#4A5568] text-sm md:text-base font-light max-w-2xl mt-3 leading-relaxed">
                Le geste sellier, raconté sans détour. Sellerie auto, nautique, moto&nbsp;: textes longs sur le réemploi, la restauration et le métier.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Filter tabs */}
      <section className="border-y border-[#1F2A4A]/10 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex gap-1 overflow-x-auto py-2.5 -mx-1 px-1">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                data-testid={`blog-filter-${cat}`}
                className={`whitespace-nowrap px-4 py-1.5 text-xs rounded-full transition-all duration-300 ${
                  category === cat
                    ? 'bg-[#1F2A4A] text-[#E8DCC8] font-medium shadow-sm'
                    : 'text-[#1F2A4A]/60 hover:text-[#1F2A4A] hover:bg-[#1F2A4A]/5'
                }`}
              >
                {CATEGORY_LABELS[cat]}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Posts grid */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-8 lg:py-12">
        {loading ? (
          <div className="grid lg:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[4/3] bg-[#1F2A4A]/5 rounded-3xl mb-3" />
                <div className="h-4 bg-[#1F2A4A]/5 rounded mb-2 w-1/3" />
                <div className="h-6 bg-[#1F2A4A]/10 rounded mb-2" />
                <div className="h-6 bg-[#1F2A4A]/10 rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-[#1F2A4A]/50 italic">Aucun article dans cette rubrique pour le moment.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10">
            {posts.map((post, idx) => {
              const featured = idx === 0 && category === 'all';
              return (
                <article
                  key={post.slug}
                  className={`group ${featured ? 'md:col-span-2 lg:col-span-3 md:grid md:grid-cols-2 md:gap-12 md:items-center' : ''}`}
                  data-testid={`blog-card-${post.slug}`}
                >
                  <Link to={L(`/blog/${post.slug}`)} className="block overflow-hidden rounded-3xl mb-4">
                    <div className={`overflow-hidden ${featured ? 'aspect-[4/3] md:aspect-[5/4]' : 'aspect-[4/3]'}`}>
                      <img
                        src={post.hero_image || 'https://images.unsplash.com/photo-1568772585407-9361f9bf3a87?w=1200&q=80'}
                        alt={post.hero_image_alt || post.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                  </Link>
                  <div>
                    <div className="flex items-center gap-3 mb-2 text-[10px] text-[#1F2A4A]/50 font-mono uppercase tracking-[0.2em]">
                      <span className="text-[#C9B896]">{CATEGORY_LABELS[post.category] || post.category}</span>
                      <span className="w-1 h-1 rounded-full bg-[#1F2A4A]/30" />
                      <span className="flex items-center gap-1.5"><Clock className="w-3 h-3" /> {post.reading_time}&nbsp;min</span>
                    </div>
                    <Link to={L(`/blog/${post.slug}`)}>
                      <h2 className={`font-light text-[#1F2A4A] leading-tight tracking-tight mb-2 group-hover:text-[#C9B896] transition-colors ${
                        featured ? 'text-3xl md:text-4xl' : 'text-xl md:text-2xl'
                      }`}>
                        {post.title}
                      </h2>
                    </Link>
                    <p className="text-[#4A5568] text-sm leading-relaxed font-light mb-3">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-[11px] text-[#1F2A4A]/45">
                      <span>{formatDate(post.published_at)} · par {post.author}</span>
                      <Link to={L(`/blog/${post.slug}`)} className="inline-flex items-center gap-1 text-[#1F2A4A] hover:text-[#C9B896] transition-colors font-medium" data-testid={`blog-read-${post.slug}`}>
                        Lire <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {/* CTA band */}
      <section className="bg-[#1F2A4A] text-[#E8DCC8] py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="font-light text-2xl md:text-4xl text-white leading-tight tracking-tight mb-4">
            Apprendre le métier à <span className="font-serif italic text-[#C9B896]">Istres.</span>
          </h3>
          <p className="text-white/65 text-sm md:text-base font-light mb-6 max-w-xl mx-auto">
            Sessions de 5 à 23 jours, 8 stagiaires maximum, certification Qualiopi. France Travail, FAFCEA, OPCO, AGEFIPH — financements possibles.
          </p>
          <Link to={L('/formations-sur-place')}>
            <Button className="bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA] font-medium px-7 py-5 rounded-full text-xs uppercase tracking-[0.2em]" data-testid="blog-cta-formations">
              Voir les formations à Istres →
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
